<template>
  <Grid
    v-model="lm"
    :total="model?.total_count"
    :columns="gridColumns"
    :data="model?.data || []"
    list-name="status_change"
    @click:row="onRowClick"
    :pagination="pagination"
    :page-size-options="pageSizeOptions"
    :persist-key="persistKey"
  >
    <template #cell="{ row, col }">
      <template v-if="col.getter">
        {{ col.getter(row, locale) }}
      </template>
      <template v-else-if="col.path">
        <template v-if="col.date && typeof _result(row, col.path) === 'string'">
          {{ commons.dateLocal(_result(row, col.path)) }}
        </template>
        <template v-else>
          {{ _result(row, col.path) }}
        </template>
      </template>
      <template v-else-if="col.date && row[col.title]">
        {{ commons.dateLocal(row[col.title]) }}
      </template>
      <template v-else>
        {{ row[col.title] }}
      </template>
    </template>
  </Grid>
</template>

<script setup>
import { computed, inject, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { result as _result } from 'lodash-es'
import { commons } from '@/lib/transformer'
import { dropdownLabel, namedEntityLabel } from '@/lib/dropdownLabel.js'
import Grid from '@/components/list/grid.vue'
import { useI18n } from 'vue-i18n'

const { t, locale } = useI18n()
const router = useRouter()

const props = defineProps({
  pagination: {
    type: Boolean,
    default: true,
  },
  initial: {
    type: Object,
    default: () => ({}),
  },
  pageSizeOptions: {
    type: Array,
    default: undefined,
  },
  persistKey: {
    type: String,
    default: '',
  },
})

const emits = defineEmits(['list-query'])

const lm = ref(props.initial || {})

const fullscreen = inject('fullscreen', false)
const model = inject('model', () => ({ data: [], total_count: 0 }))

function getStockLink(row) {
  const stockType = row.stock_type
  const stockRoutes = {
    medicine: 'stock.medicines.item',
    material_nd: 'stock.materials_nd.item',
    material_comm: 'stock.materials_comm.item',
    device: 'stock.devices.item',
    consumable: 'stock.consumables.item',
  }
  return {
    name: stockRoutes[stockType] || 'stock.medicines.item',
    params: { id: row.id },
  }
}

function onRowClick(row) {
  router.push(getStockLink(row))
}

const getName = row => {
  const stockType = row.stock_type
  if (stockType === 'medicine') {
    return namedEntityLabel(row.medicine?.medicine_trademark) || namedEntityLabel(row.medicine) || ''
  }
  return namedEntityLabel(row[stockType]) || ''
}

const getStatus = row => dropdownLabel(row.status, locale.value)

const getNewStatus = row => dropdownLabel(row.new_status, locale.value)

const getAlignedStatus = row => {
  if (!row.aligned_by || !row.aligned_by.id) {
    return t('stockStatusChange.pendingApproval')
  }
  if (row.aligned === true) {
    return t('stockStatusChange.approvedAwaitingChange')
  }
  return t('stockStatusChange.rejected')
}

const baseColumns = computed(() => [
  {
    title: 'stock_type',
    label: t('stockStatusChange.columns.type'),
    width: '120px',
    getter: row => {
      const types = {
        medicine: t('stockStatusChange.types.medicine'),
        material_nd: t('stockStatusChange.types.materialNd'),
        material_comm: t('stockStatusChange.types.materialComm'),
        device: t('stockStatusChange.types.device'),
        consumable: t('stockStatusChange.types.consumable'),
      }
      return types[row.stock_type] || row.stock_type
    },
  },
  { title: 'name', label: t('stockStatusChange.columns.name'), width: '250px', getter: getName },
  { title: 'serie', label: t('stockStatusChange.columns.batch'), width: '100px' },
  { title: 'status', label: t('stockStatusChange.columns.currentStatus'), width: '150px', getter: getStatus },
  { title: 'new_status', label: t('stockStatusChange.columns.newStatus'), width: '150px', getter: getNewStatus },
  { title: 'assign_role', label: t('stockStatusChange.columns.approver'), path: 'assign_role.caption', width: '150px' },
  {
    title: 'aligned_status',
    label: t('stockStatusChange.columns.approvalStatus'),
    width: '150px',
    getter: getAlignedStatus,
  },
])

const gridColumns = computed(() => {
  const fs = fullscreen.value
  return baseColumns.value.map(c => {
    const copy = { ...c }
    if (fs) delete copy.width
    return copy
  })
})

watch(
  lm,
  v => {
    emits('list-query', v)
  },
  { immediate: true }
)
</script>
<i18n>
{
  "ru": { "stockStatusChange": { "pendingApproval": "На согласовании", "approvedAwaitingChange": "Согласовано, ожидаем изменения статуса", "rejected": "Отказано", "types": { "medicine": "ЛП", "materialNd": "Материал НД", "materialComm": "Материал комм.", "device": "Устройство", "consumable": "Расходный" }, "columns": { "type": "Тип", "name": "Наименование", "batch": "Серия", "currentStatus": "Текущий статус", "newStatus": "Новый статус", "approver": "Согласовывает", "approvalStatus": "Статус согласования" } } },
  "en": { "stockStatusChange": { "pendingApproval": "Pending approval", "approvedAwaitingChange": "Approved; awaiting status change", "rejected": "Rejected", "types": { "medicine": "Medicine", "materialNd": "Company material", "materialComm": "Commercial material", "device": "Device", "consumable": "Consumable" }, "columns": { "type": "Type", "name": "Name", "batch": "Batch", "currentStatus": "Current status", "newStatus": "New status", "approver": "Approver", "approvalStatus": "Approval status" } } }
}
</i18n>
